import React from 'react';
import { Card, Dropdown, Tag, Button, Modal } from 'antd';
import type { MenuProps } from 'antd';
import {
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import type { Asset } from '../services/adminService';

export type AssetListCardHandlers = {
  onEdit: (asset: Asset) => void;
  onDelete: (id: number) => void;
};

type AssetListCardProps = {
  asset: Asset;
  handlers: AssetListCardHandlers;
};

const FieldRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ display: 'flex', gap: 8, padding: '4px 0', fontSize: 13 }}>
    <span style={{ color: 'rgba(0, 0, 0, 0.55)', minWidth: 90, flexShrink: 0 }}>{label}</span>
    <span style={{ minWidth: 0, wordBreak: 'break-word' }}>{children}</span>
  </div>
);

const AssetListCard: React.FC<AssetListCardProps> = ({ asset, handlers }) => {
  const menuItems: MenuProps['items'] = [
    {
      key: 'edit',
      label: 'Edit',
      icon: <EditOutlined />,
      onClick: () => handlers.onEdit(asset),
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: () =>
        Modal.confirm({
          title: 'Delete asset',
          icon: <ExclamationCircleOutlined />,
          content: 'Are you sure you want to delete this asset?',
          okText: 'Yes, delete',
          okButtonProps: { danger: true },
          cancelText: 'Cancel',
          onOk: () => handlers.onDelete(asset.id),
        }),
    },
  ];

  return (
    <Card size="small" style={{ marginBottom: 8 }} styles={{ body: { padding: 12 } }}>
      {/* Top row: name (primary identifier, clickable) + kebab */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
        <div style={{ flex: 1, minWidth: 0, fontWeight: 500, wordBreak: 'break-word' }}>
          <Link to={`/admin/assets/${asset.id}`}>{asset.name}</Link>
        </div>
        <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
          <Button
            type="text"
            icon={<MoreOutlined style={{ fontSize: 18 }} />}
            aria-label={`Actions for ${asset.name}`}
            style={{ flexShrink: 0 }}
          />
        </Dropdown>
      </div>

      {/* Body: label/value rows */}
      <FieldRow label="Organization">{asset.org_name || '\u2014'}</FieldRow>
      <FieldRow label="Type">
        {asset.asset_type ? <Tag style={{ margin: 0 }}>{asset.asset_type}</Tag> : '\u2014'}
      </FieldRow>
      <FieldRow label="Status">
        <Tag color={asset.is_active ? 'green' : 'red'} style={{ margin: 0 }}>
          {asset.is_active ? 'Active' : 'Inactive'}
        </Tag>
      </FieldRow>
    </Card>
  );
};

export default AssetListCard;
